import React, { useState, useEffect } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import {
  CreditCard,
  CheckCircle2,
  Calendar,
  Hash,
  BookOpen,
  DollarSign,
  Loader,
  Receipt,
  Search,
} from "lucide-react";
import { lmsGetStudentPaymentHistory } from "../api/lmsApi";

export default function StudentPaymentDetails() {
  const { studentId } = useParams();
  const { schoolId } = useOutletContext() || {};

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (studentId) {
      fetchPayments();
    }
  }, [studentId, schoolId]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const res = await lmsGetStudentPaymentHistory(studentId, schoolId);
      const data = res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setPayments(data);
    } catch (err) {
      console.error("Error fetching student payment history:", err);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = payments.filter((p) => {
    const q = search.toLowerCase();
    const courseName = (p.course_name || "").toLowerCase();
    const txnId = (p.transaction_id || "").toLowerCase();
    const method = (p.payment_method || "").toLowerCase();
    return courseName.includes(q) || txnId.includes(q) || method.includes(q);
  });

  const totalPaidSum = payments.reduce(
    (acc, p) => acc + (parseFloat(p.amount) || 0),
    0
  );

  return (
    <div>
      <style>{`
        .std-compact-table th {
          padding: 6px 10px !important;
          font-size: 11px !important;
          background: #f8fafc !important;
          color: #475569 !important;
          border-bottom: 1px solid #e2e8f0 !important;
          white-space: nowrap !important;
        }
        .std-compact-table td {
          padding: 4px 10px !important;
          font-size: 12px !important;
          border-bottom: 1px solid #f1f5f9 !important;
          vertical-align: middle !important;
        }
      `}</style>

      {/* Summary Stat Banner */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "12px",
          marginBottom: "14px",
        }}
      >
        <div style={{ background: "#ffffff", borderRadius: "8px", padding: "10px 14px", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "34px", height: "34px", borderRadius: "6px", background: "#eff6ff", color: "#1c498d", display: "flex", alignItems: "center", justify: "center" }}>
            <Receipt size={18} />
          </div>
          <div>
            <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>Total Transactions</div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>{payments.length}</div>
          </div>
        </div>

        <div style={{ background: "#ffffff", borderRadius: "8px", padding: "10px 14px", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "34px", height: "34px", borderRadius: "6px", background: "#f0fdf4", color: "#166534", display: "flex", alignItems: "center", justify: "center" }}>
            <DollarSign size={18} />
          </div>
          <div>
            <div style={{ fontSize: "11px", color: "#64748b", fontWeight: "600" }}>Total Fee Paid</div>
            <div style={{ fontSize: "16px", fontWeight: "700", color: "#166534" }}>₹{totalPaidSum.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Toolbar Search Box */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px", gap: "10px" }}>
        <div style={{ position: "relative", flex: 1, maxWidth: "360px" }}>
          <Search size={14} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
          <input
            type="text"
            placeholder="Search by course, Txn ID, mode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="std-field-input"
            style={{ padding: "5px 10px 5px 30px", fontSize: "12px" }}
          />
        </div>

        <div style={{ fontSize: "12px", color: "#64748b" }}>
          Showing <strong>{filteredPayments.length}</strong> of <strong>{payments.length}</strong> Payments
        </div>
      </div>

      {/* Payment Records Table */}
      <div className="std-table-card" style={{ borderRadius: "8px", overflow: "hidden" }}>
        <div style={{ width: "100%", overflowX: "auto" }}>
          <table className="std-table std-compact-table" style={{ minWidth: "800px" }}>
            <thead>
              <tr>
                <th>Txn ID & Date</th>
                <th>Course Name</th>
                <th>Paid Amount</th>
                <th>Payment Mode</th>
                <th>Status</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                    <Loader className="spinner" size={24} style={{ margin: "0 auto 8px" }} />
                    <div style={{ color: "#64748b", fontSize: "13px" }}>Loading payment history...</div>
                  </td>
                </tr>
              ) : filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "48px 20px" }}>
                    <Receipt size={40} color="#cbd5e1" style={{ margin: "0 auto 10px" }} />
                    <div style={{ fontSize: "15px", fontWeight: "600", color: "#334155" }}>
                      No Payment Records Found
                    </div>
                    <div style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px" }}>
                      No course fee payments recorded for this student yet.
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPayments.map((p, idx) => {
                  const amt = parseFloat(p.amount) || 0;
                  const payDate = p.payment_date ? new Date(p.payment_date).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  }) : "N/A";

                  const pMethod = (p.payment_method || p.payment_type || "cash").toUpperCase();

                  return (
                    <tr key={p.id || idx}>
                      <td>
                        <div>
                          <div style={{ fontWeight: "700", color: "#1c498d", fontSize: "12.5px", fontFamily: "monospace", display: "flex", alignItems: "center", gap: "5px" }}>
                            <Hash size={13} color="#94a3b8" />
                            {p.transaction_id || `TXN-${p.id}`}
                          </div>
                          <div style={{ fontSize: "11px", color: "#64748b", display: "flex", alignItems: "center", gap: "4px", marginTop: "2px" }}>
                            <Calendar size={11} color="#94a3b8" />
                            {payDate}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: "600", color: "#0f172a", fontSize: "13px", display: "flex", alignItems: "center", gap: "8px" }}>
                          <BookOpen size={14} color="#1c498d" />
                          {p.course_name || `Course #${p.course_id}`}
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: "14px", fontWeight: "700", color: "#166534" }}>
                          ₹{amt.toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "3px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "700",
                            background: pMethod.includes("ONLINE") || pMethod.includes("UPI") ? "#eff6ff" : "#f8fafc",
                            color: pMethod.includes("ONLINE") || pMethod.includes("UPI") ? "#1c498d" : "#475569",
                            border: "1px solid #cbd5e1",
                          }}
                        >
                          <CreditCard size={12} />
                          {pMethod}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "3px 8px",
                            borderRadius: "12px",
                            fontSize: "11px",
                            fontWeight: "700",
                            background: "#dcfce7",
                            color: "#15803d",
                          }}
                        >
                          <CheckCircle2 size={12} />
                          {p.status || "SUCCESS"}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: "12px", color: "#64748b", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.remarks}>
                          {p.remarks || "Course Fee Payment"}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
